import { useMemo, useState } from 'react';
import { AdSlot } from '../components/AdSlot';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { SegmentedControl } from '../components/SegmentedControl';
import { SupportButton } from '../components/SupportButton';
import { TipButton } from '../components/TipButton';
import { useToast } from '../components/Toast';
import { TodaysGarden } from '../components/TodaysGarden';
import { PlantProgress } from '../components/PlantProgress';
import { modeOfTheDay } from '../engine/featured';
import { allModes, getMode } from '../engine/registry';
import type { AnyPuzzleMode, PlayMode } from '../engine/types';
import { useToday } from '../hooks/useToday';
import { dailySlotKey, gardenFor } from '../state/garden';
import { useGame, type StartOutcome } from '../state/GameContext';
import { useNav } from '../state/NavContext';
import { useSiteConfig } from '../state/SiteConfigContext';
import { getStartPrefs, setStartPrefs } from '../state/startPrefs';
import { liveStreak, overall, type DailyRecord } from '../state/statsLogic';
import { useStats } from '../state/StatsContext';
import { accentVars } from '../utils/accent';
import { commas, countWord } from '../utils/format';
import { canNativeShare, copyText, nativeShare } from '../utils/share';
import { buildGardenText, siteUrl } from '../utils/shareText';
import { ResultModal, viewFromDaily } from './ResultModal';

function StartModal({
  mode,
  today,
  record,
  dailyParked,
  endlessParked,
  onClose,
  onStart,
  onViewResult,
}: {
  mode: AnyPuzzleMode;
  today: number;
  record: DailyRecord | undefined;
  dailyParked: boolean;
  endlessParked: boolean;
  onClose: () => void;
  onStart: (playMode: PlayMode, difficultyId: string, fresh: boolean) => void;
  onViewResult: () => void;
}) {
  const prefs = getStartPrefs(mode);
  // A finished daily can't be replayed, so open straight on Endless.
  const [playMode, setPlayMode] = useState<PlayMode>(record ? 'endless' : prefs.playMode);
  const [difficultyId, setDifficultyId] = useState(prefs.difficultyId);

  const go = (fresh: boolean) => {
    setStartPrefs(mode.id, { playMode, difficultyId });
    onStart(playMode, difficultyId, fresh);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="cv-row" style={{ gap: 'var(--space-xs)' }}>
          <Icon name={mode.glyph} size={24} />
          {mode.name}
        </span>
      }
    >
      <p className="cv-muted" style={{ marginBottom: 'var(--space-md)' }}>
        {mode.tagline}.
      </p>
      <div className="cv-stack">
        <div className="cv-field">
          <span className="cv-label">Play mode</span>
          <SegmentedControl<PlayMode>
            block
            ariaLabel="Play mode"
            value={playMode}
            onChange={setPlayMode}
            options={[
              { value: 'daily', label: record ? 'Daily ✓' : 'Daily' },
              { value: 'endless', label: 'Endless' },
            ]}
          />
          <span className="cv-help">
            {playMode === 'daily'
              ? `Daily #${today} — one shared puzzle for everyone today.`
              : 'An endless stream of fresh puzzles.'}
          </span>
        </div>

        {playMode === 'endless' ? (
          <div className="cv-field">
            <span className="cv-label">Difficulty</span>
            <SegmentedControl<string>
              block
              ariaLabel="Difficulty"
              value={difficultyId}
              onChange={setDifficultyId}
              options={mode.difficulties.map((d) => ({ value: d.id, label: d.label }))}
            />
            <span className="cv-help">{mode.difficulties.find((d) => d.id === difficultyId)?.blurb}</span>
          </div>
        ) : record ? (
          <div className="cv-start__done" data-status={record.status}>
            <Icon name={record.status === 'won' ? 'check' : 'close'} size={18} />
            <span>
              {record.status === 'won' ? 'Solved' : 'Missed'} today’s daily ·{' '}
              <strong className="cv-mono">{commas(record.score)}</strong>
            </span>
          </div>
        ) : (
          <p className="cv-help">Daily plays at the standard set so everyone shares the same puzzle.</p>
        )}
      </div>

      <div className="cv-modal__footer cv-stack" style={{ gap: 'var(--space-xs)' }}>
        {playMode === 'daily' ? (
          record ? (
            <Button variant="primary" block iconLeft="trophy" onClick={onViewResult}>
              View today’s result
            </Button>
          ) : (
            <Button variant="primary" block iconLeft="play" onClick={() => go(false)}>
              {dailyParked ? 'Continue today’s daily' : `Play Daily #${today}`}
            </Button>
          )
        ) : endlessParked ? (
          <>
            <Button variant="primary" block iconLeft="play" onClick={() => go(false)}>
              Continue endless
            </Button>
            <Button variant="ghost" block iconLeft="shuffle" onClick={() => go(true)}>
              New puzzle
            </Button>
          </>
        ) : (
          <Button variant="primary" block iconLeft="play" onClick={() => go(true)}>
            Play {mode.name}
          </Button>
        )}
      </div>
    </Modal>
  );
}

export function HomeScreen() {
  const { isModeEnabled, config: siteConfig } = useSiteConfig();
  const modes = allModes().filter((m) => isModeEnabled(m.id));
  const game = useGame();
  const { navigate } = useNav();
  const { stats, modeStats } = useStats();
  const toast = useToast();
  const today = useToday();
  const [selected, setSelected] = useState<AnyPuzzleMode | null>(null);
  const [viewing, setViewing] = useState<AnyPuzzleMode | null>(null);
  const o = overall(stats, today);
  const featured = siteConfig.showModeOfDay ? modeOfTheDay(today, modes) : null;

  const parkedKeys = useMemo(() => new Set(game.parked.map((p) => p.key)), [game.parked]);
  const garden = gardenFor(stats, today, modes, parkedKeys);

  // "Continue": the puzzle on screen, else the most recently touched parked one.
  const activePlaying = game.active && game.active.status === 'playing' ? game.active : null;
  const parkedTop = game.parked.find((p) => getMode(p.modeId) && isModeEnabled(p.modeId));
  const resume = activePlaying
    ? {
        key: activePlaying.key,
        mode: activePlaying.mode,
        label: activePlaying.playMode === 'daily' ? `Daily #${activePlaying.seed.dayNumber}` : 'Endless',
      }
    : parkedTop
      ? {
          key: parkedTop.key,
          mode: getMode(parkedTop.modeId)!,
          label: parkedTop.playMode === 'daily' ? `Daily #${parkedTop.day}` : 'Endless',
        }
      : null;

  const report = (outcome: StartOutcome, mode: AnyPuzzleMode) => {
    if (outcome === 'done') setViewing(mode);
    else if (outcome !== 'unavailable') navigate('play');
  };

  const shareGarden = async () => {
    const text = buildGardenText({
      day: today,
      plots: garden.entries.map((e) => e.plot),
      streak: o.garden.current,
      url: siteUrl(),
    });
    if (canNativeShare() && (await nativeShare(text))) return;
    const ok = await copyText(text);
    toast.show(ok ? 'Garden copied to clipboard' : 'Could not copy', ok ? 'success' : 'error');
  };

  const viewRecord = viewing ? stats.dailies[String(today)]?.[viewing.id] : undefined;

  return (
    <div className="cv-home">
      <header className="cv-home__hero">
        <h1 className="cv-home__title">
          Clue<em>Vine</em>
        </h1>
        <p className="cv-home__tagline">
          A daily word garden — {countWord(modes.length)} ways to play, one growing streak.
        </p>
        <PlantProgress streak={o.currentBestStreak} compact />
        <div className="cv-row cv-wrap" style={{ gap: 'var(--space-2xs)', justifyContent: 'center' }}>
          <Button size="sm" variant="secondary" iconLeft="help" onClick={() => navigate('howto')}>
            How to play
          </Button>
          <SupportButton />
          <TipButton />
        </div>
      </header>

      {resume ? (
        <button
          className="cv-card cv-card--interactive"
          style={accentVars(resume.mode.accent)}
          aria-label={`Continue ${resume.mode.name}, ${resume.label} in progress`}
          onClick={() => {
            if (game.resume(resume.key)) navigate('play');
          }}
        >
          <div className="cv-row-between">
            <div className="cv-row" style={{ gap: 'var(--space-sm)' }}>
              <span className="cv-mode-card__glyph">
                <Icon name={resume.mode.glyph} />
              </span>
              <div>
                <div className="cv-mode-card__name" style={{ fontSize: 'var(--text-lg)' }}>
                  Continue {resume.mode.name}
                </div>
                <div className="cv-mode-card__tag">{resume.label} in progress</div>
              </div>
            </div>
            <Icon name="play" />
          </div>
        </button>
      ) : null}

      {modes.length > 0 ? (
        <TodaysGarden
          garden={garden}
          streak={o.garden.current}
          atRisk={o.garden.atRisk}
          onPick={(mode) => {
            if (stats.dailies[String(today)]?.[mode.id]) setViewing(mode);
            else report(game.start({ modeId: mode.id, playMode: 'daily' }), mode);
          }}
          onShare={shareGarden}
        />
      ) : null}

      {featured ? (
        <button
          className="cv-card cv-card--interactive cv-featured"
          style={accentVars(featured.accent)}
          aria-label={`Play today's featured mode, ${featured.name}`}
          onClick={() => setSelected(featured)}
        >
          <div className="cv-row-between">
            <div className="cv-row" style={{ gap: 'var(--space-sm)' }}>
              <span className="cv-mode-card__glyph">
                <Icon name={featured.glyph} size={26} />
              </span>
              <div>
                <div className="cv-eyebrow">Mode of the day</div>
                <div className="cv-mode-card__name" style={{ fontSize: 'var(--text-lg)' }}>
                  {featured.name}
                </div>
                <div className="cv-mode-card__tag">{featured.tagline}</div>
              </div>
            </div>
            <Icon name="play" />
          </div>
        </button>
      ) : null}

      <div className="cv-modes">
        {modes.map((mode) => {
          const ms = modeStats(mode.id);
          const streak = liveStreak(ms, today);
          const rec = stats.dailies[String(today)]?.[mode.id];
          const growing = !rec && parkedKeys.has(dailySlotKey(mode.id, today));
          return (
            <button
              key={mode.id}
              className="cv-card cv-card--interactive cv-mode-card"
              style={accentVars(mode.accent)}
              onClick={() => setSelected(mode)}
            >
              <div className="cv-mode-card__top">
                <span className="cv-mode-card__glyph">
                  <Icon name={mode.glyph} size={26} />
                </span>
                <div>
                  <div className="cv-mode-card__name">{mode.name}</div>
                  <div className="cv-mode-card__tag">{mode.tagline}</div>
                </div>
              </div>
              <p className="cv-mode-card__desc">{mode.description}</p>
              <div className="cv-mode-card__meta">
                {rec ? (
                  <span className="cv-chip" data-status={rec.status}>
                    <Icon name={rec.status === 'won' ? 'check' : 'close'} size={13} />{' '}
                    {rec.status === 'won' ? 'Solved today' : 'Missed today'}
                  </span>
                ) : growing ? (
                  <span className="cv-chip cv-chip--seed">In progress</span>
                ) : (
                  <span className="cv-chip">Daily #{today}</span>
                )}
                {streak > 0 ? (
                  <span className="cv-chip cv-chip--seed" title={`${streak}-day ${mode.name} streak`}>
                    <Icon name="flame" size={13} /> {streak}
                  </span>
                ) : null}
                {ms.played > 0 ? (
                  <span className="cv-faint" style={{ fontSize: 'var(--text-xs)' }}>
                    {ms.played} played
                  </span>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>

      <AdSlot />

      {selected ? (
        <StartModal
          mode={selected}
          today={today}
          record={stats.dailies[String(today)]?.[selected.id]}
          dailyParked={parkedKeys.has(dailySlotKey(selected.id, today))}
          endlessParked={parkedKeys.has(`${selected.id}:e`)}
          onClose={() => setSelected(null)}
          onViewResult={() => {
            setViewing(selected);
            setSelected(null);
          }}
          onStart={(playMode, difficultyId, fresh) => {
            const mode = selected;
            setSelected(null);
            report(game.start({ modeId: mode.id, playMode, difficultyId, fresh }), mode);
          }}
        />
      ) : null}

      {viewing && viewRecord ? (
        viewRecord.result ? (
          <ResultModal
            view={viewFromDaily(viewing, today, false, viewRecord.result)}
            onClose={() => setViewing(null)}
            onHome={() => setViewing(null)}
            onPlayAgain={() => {
              const mode = viewing;
              setViewing(null);
              report(game.start({ modeId: mode.id, playMode: 'endless', difficultyId: getStartPrefs(mode).difficultyId }), mode);
            }}
          />
        ) : (
          <Modal open onClose={() => setViewing(null)} title={`${viewing.name} · Daily #${today}`}>
            <p className="cv-muted">
              {viewRecord.status === 'won' ? 'Solved' : 'Missed'} · {commas(viewRecord.score)} points.
            </p>
          </Modal>
        )
      ) : null}
    </div>
  );
}
